/**
 * Chat Image Service
 * Handles uploading and retrieving chat images from Supabase storage
 */

import { supabase } from '../lib/supabaseClient';
import {
  compressChatImage,
  dataUrlToBlob,
  generateChatImageFilename,
  validateImageSize,
  CompressedImage
} from '../utils/chatImageCompression';

const BUCKET_NAME = 'chat-images';
const MAX_IMAGE_SIZE_KB = 3072; // 3MB max after compression (allows good quality images)

/**
 * Upload chat image to Supabase storage
 */
export const uploadChatImage = async (dataUrl: string): Promise<string> => {
  console.log('📤 [ChatImage] Starting upload...');
  
  try {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');
    
    // Compress image
    console.log('🔧 [ChatImage] Compressing image...');
    const compressed: CompressedImage = await compressChatImage(dataUrl);
    
    // Validate size
    if (!validateImageSize(compressed.sizeKB, MAX_IMAGE_SIZE_KB)) {
      throw new Error(`Image too large: ${compressed.sizeKB}KB (max: ${MAX_IMAGE_SIZE_KB}KB)`);
    }
    
    // Convert to blob
    const blob = dataUrlToBlob(compressed.dataUrl);
    
    // Generate filename
    const filename = generateChatImageFilename(user.id);
    
    // Upload to Supabase storage
    console.log(`📤 [ChatImage] Uploading to storage: ${filename}`);
    const { data, error } = await supabase.storage
      .from(BUCKET_NAME)
      .upload(filename, blob, {
        contentType: 'image/webp',
        cacheControl: '3600',
        upsert: false
      });
    
    if (error) {
      console.error('❌ [ChatImage] Upload failed:', error);
      throw error;
    }
    
    // Get public URL
    const { data: urlData } = supabase.storage
      .from(BUCKET_NAME)
      .getPublicUrl(data.path);
    
    console.log(`✅ [ChatImage] Upload successful: ${compressed.sizeKB}KB`);
    
    return urlData.publicUrl;
  } catch (error) {
    console.error('❌ [ChatImage] Error:', error);
    throw error;
  }
};

/**
 * Get signed URL for chat image (for private images)
 */
export const getChatImageUrl = async (path: string): Promise<string> => {
  try {
    const { data, error } = await supabase.storage
      .from(BUCKET_NAME)
      .createSignedUrl(path, 3600); // 1 hour expiry
    
    if (error) throw error;
    
    return data.signedUrl;
  } catch (error) {
    console.error('❌ [ChatImage] Failed to get signed URL:', error);
    throw error;
  }
};

/**
 * Delete chat image
 */
export const deleteChatImage = async (path: string): Promise<void> => {
  try {
    const { error } = await supabase.storage
      .from(BUCKET_NAME)
      .remove([path]);
    
    if (error) throw error;
    
    console.log('✅ [ChatImage] Image deleted:', path);
  } catch (error) {
    console.error('❌ [ChatImage] Failed to delete image:', error);
    throw error;
  }
};

/**
 * Extract storage path from full URL
 */
export const getPathFromUrl = (url: string): string => {
  const bucketPath = `/storage/v1/object/public/${BUCKET_NAME}/`;
  const signedPath = `/storage/v1/object/sign/${BUCKET_NAME}/`;
  
  if (url.includes(bucketPath)) {
    return url.split(bucketPath)[1].split('?')[0];
  } else if (url.includes(signedPath)) {
    return url.split(signedPath)[1].split('?')[0];
  }
  
  return url;
};
